import { RouterLink } from '@/common/components/router-links/RouterLink';
import { Trans } from '@lingui/react/macro';
import { Loader2, MailCheck, MailX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { AccountService } from '@/features/auth/services/AccountService';

const RESEND_COOLDOWN_SECONDS = 60;

const resendEmailSchema = z.object({
  email: requiredInput(z.email()),
});

interface ConfirmEmailPageProps {
  uidb64: string;
  token: string;
}

export const ConfirmEmailPage = ({ uidb64, token }: ConfirmEmailPageProps) => {
  const [cooldown, setCooldown] = useState(0);

  const verifyMutation = useMutation({
    mutationFn: () => AccountService.verifyEmail({ uidb64, token }),
  });

  useEffect(() => {
    verifyMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resendMutation = useMutation({
    mutationFn: (emailToSend: string) =>
      AccountService.resendVerificationEmail({ email: emailToSend }),
    onSuccess: () => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
    },
  });

  const form = useAppForm(
    appFormOptions({
      schema: resendEmailSchema,
      defaultValues: { email: null },
      onSubmit: async ({ value }) => {
        await resendMutation.mutateAsync(value.email);
      },
    })
  );

  if (verifyMutation.isPending || verifyMutation.isIdle) {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="mt-4 flex size-16 items-center justify-center rounded-full bg-blue-50">
          <Loader2 size={32} className="animate-spin text-blue-600" />
        </div>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Verifying your email…</Trans>
        </h1>
        <p className="mt-1 max-w-sm text-sm text-gray-500">
          <Trans>Please wait a moment.</Trans>
        </p>
      </div>
    );
  }

  if (verifyMutation.isSuccess) {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="mt-4 flex size-16 items-center justify-center rounded-full bg-green-50">
          <MailCheck size={32} className="text-green-600" />
        </div>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Email verified!</Trans>
        </h1>
        <p className="mt-1 max-w-sm text-sm text-gray-500">
          <Trans>
            Your email address has been successfully verified. You can now log in to your account.
          </Trans>
        </p>
        <p className="mt-8 text-sm">
          <RouterLink to="/login">
            <Trans>Sign in to your account</Trans>
          </RouterLink>
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="mt-4 flex size-16 items-center justify-center rounded-full bg-red-50">
        <MailX size={32} className="text-red-600" />
      </div>
      <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
        <Trans>Verification failed</Trans>
      </h1>
      <p className="mt-1 max-w-sm text-sm text-gray-500">
        <Trans>
          The verification link is invalid or has expired. Please request a new verification email.
        </Trans>
      </p>

      <div className="mt-6 w-full">
        {resendMutation.isSuccess ? (
          <p className="mb-3 text-sm font-medium text-green-600">
            <Trans>Verification email sent!</Trans>
          </p>
        ) : (
          <form.AppForm>
            <form.FormElement>
              <FormErrors className="mb-4" />
              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label={<Trans>Email</Trans>}
                    type="email"
                    placeholder="name@company.com"
                  />
                )}
              </form.AppField>
              <FormSubmit className="mt-4 w-full" disabled={cooldown > 0}>
                {cooldown > 0 ? (
                  <Trans>Resend email in {cooldown}s</Trans>
                ) : (
                  <Trans>Resend verification email</Trans>
                )}
              </FormSubmit>
            </form.FormElement>
          </form.AppForm>
        )}
      </div>

      <p className="mt-8 text-sm text-gray-500">
        <Trans>Already verified?</Trans>{' '}
        <RouterLink to="/login">
          <Trans>Sign in</Trans>
        </RouterLink>
      </p>
    </div>
  );
};
